<template>
  <div :class="{ container: $props.is_container }">
    <div class="box mt-0">
      <div class="columns">
        <div class="column is-5">
          <h1 class="title is-4">{{ $props.title || $t("title") }}</h1>
        </div>
        <div class="column is-7">
          <p class="buttons is-pulled-right">
            <slot name="buttons"></slot>
            <button
              :class="'button ' + extra_button.class"
              @click="extra_button.click"
              v-for="(extra_button, eidx) in $props.extra_buttons"
              :key="eidx"
              :data-tooltip="extra_button.tooltip"
            >
              <span class="icon">
                <i :class="extra_button.icon"></i>
              </span>
              <span>{{ extra_button.text }}</span>
            </button>
            <button
              class="button is-primary is-rounded"
              @click="refresh"
              :disabled="is_loading"
              v-bind:class="{ 'is-loading': is_loading }"
              :data-tooltip="$t('refresh')"
              v-show="$props.show_refresh"
            >
              <span class="icon">
                <i class="fa fa-refresh"></i>
              </span>
              <span>{{ $t("refresh") }}</span>
            </button>
          </p>
        </div>
      </div>
      <div class="table-container mt-5" ref="tc">
        <div
          class="columns is-multiline"
          v-show="mode == 'columns'"
          ref="columns"
        >
          <div
            class="column is-4-tablet is-4-desktop"
            v-for="(item, ridx) in data_loaded"
            :key="ridx"
          >
            <slot name="item" v-bind="item"></slot>
          </div>
        </div>
        <div :class="'grid ' + grid_class" v-show="mode == 'grid'" ref="grid">
          <div class="cell" v-for="(item, ridx) in data_loaded" :key="ridx">
            <slot name="item" v-bind="item"></slot>
          </div>
        </div>
        <div class="columns" v-show="mode == 'list'">
          <div class="column" v-for="(item, ridx) in data_loaded" :key="ridx">
            <slot name="item" v-bind="item"></slot>
          </div>
        </div>
        <table
          class="table is-striped is-hoverable"
          ref="table"
          style="width: 100%"
          v-show="mode == 'table'"
        >
          <thead>
            <tr>
              <th v-for="(column, cidx) in $props.columns" :key="cidx">
                {{ column.name }}
              </th>
            </tr>
          </thead>
          <tbody></tbody>
          <tfoot v-if="$props.column_search">
            <tr>
              <th v-for="(column, cidx) in $props.columns" :key="cidx"></th>
            </tr>
          </tfoot>
        </table>
        <div v-show="mode == 'grouped-table'" ref="grouped">
          <div class="tabs is-fullwidth is-toggle">
            <ul>
              <li
                :class="{ 'is-active': selected_group == key }"
                v-for="(group, key) in groups"
                :key="key"
                @click="select_group(key)"
              >
                <a v-html="key"></a>
              </li>
            </ul>
          </div>
          <div
            class=""
            v-show="selected_group == key"
            v-for="(group, key) in groups"
            :key="key"
          >
            <table
              class="table is-striped is-hoverable"
              ref="table"
              style="width: 100%"
            >
              <thead>
                <tr>
                  <th v-for="(column, cidx) in $props.columns" :key="cidx">
                    {{ column.title }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, ridx) in group" :key="ridx">
                  <td
                    v-for="(column, cidx) in $props.columns"
                    :key="cidx"
                    v-html="column.data(item)"
                  ></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div v-show="mode == 'grouped-table-vertical'" ref="grouped">
          <div class="columns">
            <div class="column is-4">
              <aside class="menu">
                <p class="menu-label">{{ $t("select group") }}</p>
                <div class="field">
                  <div class="control">
                    <input
                      class="input"
                      type="text"
                      v-model="search_group"
                      :placeholder="$t('filter')"
                    />
                  </div>
                </div>
                <ul class="menu-list">
                  <li
                    v-for="(group, key) in groups"
                    :key="key"
                    @click="select_group(key)"
                  >
                    <a
                      v-html="key"
                      :class="{ 'is-active': selected_group == key }"
                    ></a>
                  </li>
                </ul>
              </aside>
            </div>
            <div class="column">
              <div
                class=""
                v-show="selected_group == key"
                v-for="(group, key) in groups"
                :key="key"
              >
                <div class="p-3" v-html="key"></div>
                <table
                  class="table is-striped is-hoverable"
                  ref="table"
                  style="width: 100%"
                >
                  <thead>
                    <tr>
                      <th v-for="(column, cidx) in $props.columns" :key="cidx">
                        {{ column.title }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, ridx) in group" :key="ridx">
                      <td
                        v-for="(column, cidx) in $props.columns"
                        :key="cidx"
                        v-html="column.data(item)"
                      ></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { api_host, session_token, error_message, get_user_data } from "@/utils";
import DataTable from "datatables.net-dt";
// import "datatables.net-bm/js/dataTables.bulma";
import "datatables.net-dt/css/dataTables.dataTables.min.css";
// import "datatables.net-bm/css/dataTables.bulma.min.css";
import lang_es from "datatables.net-plugins/i18n/es-ES";
import lang_fr from "datatables.net-plugins/i18n/fr-FR";
import { groupBy } from "lodash";

export default {
  name: "GenericTable",
  props: {
    show_refresh: {
      type: Boolean,
      required: false,
      default: () => {
        return true;
      },
    },
    columns: {
      type: Array,
      required: true,
    },
    title: {
      type: String,
      required: false,
    },
    column_search: {
      type: Boolean,
      required: false,
      default: () => {
        return true;
      },
    },
    resource_url: {
      type: String,
      required: true,
    },
    extra_buttons: {
      type: Array,
      required: false,
      default: () => {
        return [];
      },
    },
    extra_data: {
      type: Object,
      required: false,
      default: () => {
        return {};
      },
    },
    on_created_row: {
      type: Function,
      required: false,
      default: () => {},
    },
    page_length: {
      type: Number,
      required: false,
      default: () => {
        return 10;
      },
    },
    mode: {
      type: String,
      required: false,
      default: () => {
        return "table";
      },
    },
    grid_class: {
      type: String,
      required: false,
      default: () => {
        return "is-col-min-7";
      },
    },
    searching: {
      type: Boolean,
      required: false,
      default: () => {
        return true;
      },
    },
    is_container: {
      type: Boolean,
      required: false,
      default: () => {
        return true;
      },
    },
    options: {
      type: Object,
      required: false,
      default: () => {
        return {
          dom: null,
          layout: {
            topStart: "pageLength",
            topEnd: "search",
            bottomStart: "info",
            bottomEnd: "paging",
          },
          grouping: () => {},
        };
      },
    },
  },
  computed: {
    // groups() {
    //   let v = this;
    //   return groupBy(v.data_loaded, v.options.grouping);
    // },
  },
  components: {},
  data() {
    return {
      is_loading: false,
      datatables_instance: null,
      data_loaded: [],
      selected_group: "",
      search_group: "",
      groups: {},
    };
  },
  methods: {
    refresh() {
      let v = this;
      v.selected_group = "";
      v.is_loading = true;
      if (v.datatables_instance) {
        v.datatables_instance.ajax.reload();
        v.is_loading = false;
      } else v.init();
    },
    init() {
      let v = this;
      // console.log("initing", `${api_host}${v.$props.resource_url}`);
      let current_locale = v.$i18n.locale;

      let language = "en";
      if (current_locale == "es") language = lang_es;
      if (current_locale == "fr") language = lang_fr;

      v.is_loading = true;
      let table = new DataTable(this.$refs.table, {
        pageLength: v.page_length,
        searching: v.$props.searching,
        processing: true,
        serverSide: true,
        language: language,
        info: false,
        layout: v.$props.options.layout,
        initComplete: function (settings, json) {
          v.$emit("init_complete", { settings, json });
          if (v.$props.column_search)
            this.api()
              .columns()
              .every(function (index) {
                let column = this;
                // Validate if column is searchable
                let ctxt = column.context.find((x) => x);
                if (ctxt) {
                  let is_searchable = ctxt.aoColumns[index].bSearchable;
                  if (is_searchable) {
                    let title = column.header().textContent;

                    // Create input element
                    let input = document.createElement("input");
                    input.placeholder = `${v.$t("search by")} ${title}`;
                    input.className = "input";
                    column.footer().replaceChildren(input);

                    // Event listener for user input
                    input.addEventListener("keyup", () => {
                      // console.log("input", input.value, column.search());
                      column.search(input.value).draw();
                    });
                  }
                }
              });
        },
        ajax: {
          url: `${api_host}${v.$props.resource_url}`,
          // timeout: timeout,
          // type: "get",
          beforeSend: function (request) {
            let token = session_token();
            let user_data = get_user_data();
            // console.log("token", token);
            if (token) {
              request.setRequestHeader("modul-access-token", token);
              request.setRequestHeader("enterprise", user_data.enterprise_name);
            }
          },
          dataSrc: function (json) {
            v.$emit("data_loaded", json);
            v.data_loaded = json;
            v.is_loading = false;

            if (v.options && v.options.grouping)
              v.groups = groupBy(v.data_loaded, v.options.grouping);

            setTimeout(() => {
              let first_group = Object.keys(v.groups)[0];
              if (first_group) v.selected_group = first_group;
            }, 500);

            return json;
          },
          error: function (xhr) {
            if (xhr.status == 511) {
              localStorage.clear();
              let rr = `/login?redir=${window.location.pathname}${window.location.search}`;
              window.location = rr;
            } else {
              error_message({
                status: xhr.status,
                text: xhr.statusText,
                data: xhr.responseText.replace(/\n/g, ""),
              });
            }
          },
          data: v.$props.extra_data,
        },
        columns: v.$props.columns,
        createdRow: function (row, data) {
          v.$props.on_created_row(row, data);
          window.jQuery(row).on("click", function () {
            v.$emit("selected", { row, data });
          });
        },
        drawCallback(/* settings */) {
          // console.log("drawCallback", settings);
          // let table_container = window.jQuery(".dt-container");
          let table_wrapper = window.jQuery(".dt-layout-table");
          table_wrapper.after(v.$refs.grid);
        },
      });
      table.on("error.dt", function (e, settings, techNote, message) {
        console.error("An error has been reported by DataTables: ", message);
      });
      v.datatables_instance = table;

      // setTimeout(() => {
      //   window
      //     .jQuery(v.$refs.tc)
      //     .find(".dt-length select.dt-input")
      //     .addClass("select");
      //   window
      //     .jQuery(v.$refs.tc)
      //     .find(".dt-search input.dt-input")
      //     .addClass("input");
      // }, 200);
    },
    destroy() {
      let v = this;
      if (v.datatables_instance) {
        v.datatables_instance.clear();
        v.datatables_instance.destroy();
        v.datatables_instance = null;
      }
    },
    select_group(key) {
      let v = this;
      v.selected_group = key;
    },
  },
  mounted() {},
  created() {},
  watch: {
    search_group: function (val) {
      let v = this;
      let groups = groupBy(v.data_loaded, v.options.grouping);
      let filtered = {};
      for (let key in groups) {
        if (key.toLowerCase().includes(val.toLowerCase())) {
          filtered[key] = groups[key];
        }
      }
      v.groups = filtered;
    },
  },
};
</script>

<style>
.table-container .dt-length div.select {
  width: 70px;
}
.table-container .dt-length div.select select {
  width: 100%;
}
</style>
